'use client'

import Link from "next/link";
import { useLabel } from '@/helpers/useLabel';
import type { ComponentType } from 'react';
import { Globe, Mail } from 'lucide-react';
import { FaDiscord, FaFacebookF, FaInstagram, FaTiktok, FaXTwitter, FaYoutube } from 'react-icons/fa6';
import { settings } from '../../config/settings';
import ThemeSwitcher from './ThemeSwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

type SocialIcon = ComponentType<{ className?: string }>;

const socialIconMap: Record<string, SocialIcon> = {
    discord: FaDiscord,
    x: FaXTwitter,
    instagram: FaInstagram,
    facebook: FaFacebookF,
    tiktok: FaTiktok,
    youtube: FaYoutube,
    email: Mail,
};

const socialLabelMap: Record<string, string> = {
    discord: 'Discord',
    x: 'X',
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    email: 'Email',
};

function normalizeSocialLink(platform: string, rawLink?: string) {
    if (!rawLink) {
        return '#';
    }

    const normalized = rawLink.trim();
    const isEmailLike = normalized.includes('@') && !normalized.startsWith('http');

    if (platform === 'email' || isEmailLike) {
        return normalized.startsWith('mailto:') ? normalized : `mailto:${normalized}`;
    }

    if (
        normalized.startsWith('http://') ||
        normalized.startsWith('https://') ||
        normalized.startsWith('mailto:')
    ) {
        return normalized;
    }

    return `https://${normalized}`;
}

export default function Footer() {
    const t = useTranslations('Footer');
    const tl = useLabel();

    const socialLinks = Object.entries(settings.socials?.other || {}).map(([platform, social]) => {
        const href = normalizeSocialLink(platform, social?.link);
        const Icon = socialIconMap[platform] || (href.startsWith('mailto:') ? Mail : Globe);
        const label = tl(socialLabelMap[platform] || platform);

        return {
            platform,
            href,
            Icon,
            label,
        };
    });

    const groups = settings.footer_links.map((group) => {
        const links = group.links.map((link, index) => (
            <Link
                key={index}
                href={link.link}
                target={link.link.startsWith('http') ? '_blank' : '_self'}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
                {tl(link.label)}
            </Link>
        ));

        return (
            <div key={group.title} className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground/90">{tl(group.title)}</h4>
                <div className="flex flex-col gap-2">
                    {links}
                </div>
            </div>
        );
    });


    return (
        <footer className="px-4 sm:px-6 lg:px-8 pb-8">
            <Card className="border-border/60 bg-card">
                <CardContent className="p-6 sm:p-8">
                    <div className="grid gap-10 lg:grid-cols-[1.2fr_2fr]">
                        <div className="space-y-4">
                            <Link href="/" className="inline-flex items-center">
                                <img src="/apect-logo.png" alt="Apect" className="h-9 w-auto object-contain" />
                            </Link>
                            <p className="max-w-md text-sm text-muted-foreground">
                                {t('description')}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {socialLinks.map((social) => (
                                    <Link
                                        key={social.platform}
                                        href={social.href}
                                        target={social.href.startsWith('mailto:') ? '_self' : '_blank'}
                                        rel={social.href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                                    >
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            aria-label={social.label}
                                            title={social.label}
                                        >
                                            <social.Icon className="h-4 w-4" />
                                        </Button>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                            {groups}
                        </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-muted-foreground">{t('copyright')}</p>
                        <div className="flex items-center gap-2">
                            {settings.color_scheme.force_color_scheme === "auto" && <ThemeSwitcher />}
                            {settings.translation_system.enabled && <LanguageSwitcher />}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </footer>
    );
}
